import { useRef, useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
// API's
import { recalculateWashout } from "@/Washouts/api/common/mutations";
// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useFactoryWashout } from "@/Washouts/hooks/useFactoryWashout";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
// Helpers
import { handleWashoutSubscriptions } from "@/Washouts/helpers/washoutSubscriptions";
import useApproveRetractWashoutSubscription from "@/Washouts/hooks/WashoutReview/useApproveRetractWashoutSubscription";
import useApproveAllWashoutSubscription from "@/Washouts/hooks/common/useApproveAllWashoutSubscription";

// Constants
import actionsList from "@/Constants/actionsList";
// Custom components
import RoundedBox from "@/Layouts/components/Basics/RoundedBox";
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";
import FinanceManagerWashoutContent from "./FinanceManagerWashoutContent";
import UpdatedOn from "@/Washouts/components/UpdatedOn";
// Images
import empty from "@/assets/images/commons/empty.png";
import emptyTable from "@/assets/images/commons/empty_table.png";

const positionLabel = "F&I Manager";

export default function FinanceManagerWashoutContainer(props) {
	const { location, selected, subscriptionsInformation, updateSidePanelCallBack, activeCommissionCycles = [] } = props;

	const queryClient = useQueryClient();

	const { useWashoutQueries } = useFactoryWashout();
	const washoutQueries = useWashoutQueries(positionLabel);

	const selectedRef = useRef(selected);

	const { getSubscription } = useSubscriptions();

	const { data: model, isLoading } = useQuery({
		queryKey: [
			"getWashout",
			props.selected?.payPeriodId,
			props.selected?.position,
			props.selected?.userId,
			props.selected?.commissionCycleId,
		],
		queryFn: async () => {
			const { locationId } = props.location || {};
			const { userId, commissionCycleId, payPeriodId, position } = props.selected || {};
			return washoutQueries?.getWashout(locationId, userId, commissionCycleId, payPeriodId, position);
		},
		enabled: !!props.location && !!props.selected,
		staleTime: Infinity,
	});

	const { mutate: recalculate } = useMutation({
		mutationKey: ["recalculateWashout"],
		mutationFn: (params) => recalculateWashout(params),
	});

	useApproveRetractWashoutSubscription({
		locationId: props.location?.locationId,
		commissionCycleId: props.selected?.commissionCycleId,
		payPeriodId: props.selected?.payPeriodId,
		updateWashoutModel: true,
	});

	useApproveAllWashoutSubscription({
		locationId: props.location?.locationId,
		commissionCycleId: props.selected?.commissionCycleId,
		payPeriodId: props.selected?.payPeriodId,
	});

	const { useAuth } = useAuthorization();
	const overrideCompensation = useAuth(actionsList.OVERRIDE_FI_MANAGER_COMPENSATION || actionsList["OVERRIDE_F&I_MANAGER_COMPENSATION"]);

	const handleSummaryChange = (input) => {
		if (!input || !location || !model) {
			return;
		}

		recalculate({
			input,
			position: model?.employee?.position || positionLabel,
			isDepartmental: false,
			locationId: location.locationId,
			washoutId: model._id,
		});
	};

	const subsPayload = useMemo(
		() => ({
			activeCommissionCycles,
			subscriptionsInformation,
			locationId: location?.locationId,
			userId: selected?.userId,
			payPeriodId: selected?.payPeriodId,
			commissionCycleId: selected?.commissionCycleId,
			position: selected?.position,
			positionWithoutSpaces: "financeManager",
		}),
		[
			location?.locationId,
			selected?.userId,
			selected?.payPeriodId,
			selected?.commissionCycleId,
			selected?.position,
			activeCommissionCycles,
			subscriptionsInformation,
		]
	);

	useEffect(() => {
		if (!subsPayload.locationId || !subsPayload.userId) {
			return;
		}

		handleWashoutSubscriptions({
			subscriptionsInformation: subsPayload.subscriptionsInformation,
			props: {
				location,
				selected,
				positionWithoutSpaces: subsPayload.positionWithoutSpaces,
				activeCommissionCycles: subsPayload.activeCommissionCycles,
			},
			actions: {
				queryClient,
				getSubscription,
			},
			options: {
				useRanking: true,
			},
		});
	}, [subsPayload, queryClient, getSubscription]);

	useEffect(() => {
		if (selected && selectedRef.current !== selected) {
			updateSidePanelCallBack?.(selected);
		}
		selectedRef.current = selected;
	}, [selected, updateSidePanelCallBack]);

	if (!model && !isLoading) {
		return (
			<RoundedBox>
				<EmptyBody
					image={props.selected ? emptyTable : empty}
					title={props.selected ? "No washout found for this manager" : "Select an F&I Manager to view their washout"}
					bodyHeight={"510px"}
					sx={{
						boxShadow: "none !important",
					}}
				/>
			</RoundedBox>
		);
	}

	return (
		<>
			<FinanceManagerWashoutContent
				model={model}
				locationId={props.location.locationId}
				isLoading={isLoading}
				overrideCompensation={overrideCompensation}
				selected={props.selected}
				handleSummaryChange={handleSummaryChange}
			/>

			<UpdatedOn isLoading={isLoading} updatedOn={model?.created} />
		</>
	);
}

import PropTypes from "prop-types";
FinanceManagerWashoutContainer.propTypes = {
	model: PropTypes.shape({
		productConversions: PropTypes.array,
		callActivityPerformance: PropTypes.array,
	}),
	location: PropTypes.shape({
		locationId: PropTypes.string,
	}),
	selected: PropTypes.shape({
		userId: PropTypes.string,
		commissionCycleId: PropTypes.string,
		payPeriodId: PropTypes.string,
		status: PropTypes.string,
		position: PropTypes.string,
	}),
	subscriptionsInformation: PropTypes.object,
	updateSidePanelCallBack: PropTypes.func,
	activeCommissionCycles: PropTypes.array,
};
